import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import {
  BillableIcon,
  ClientFilter,
  DateTime,
  FiltersBar,
  Hours,
  IconButton,
  InternalClientTooltip,
  ListView,
  ListViewActions,
  Page,
  ProjectAdmins,
  ProjectLink,
  ProjectStatusFilter,
  SearchInput,
  SingleSelect,
  Tag,
  Tags,
  Tooltip,
} from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import AssignProjectModal from './AssignProjectModal';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Status = styled.span`
  color: ${({ status }) =>
    ({
      tentative: colors.grey25,
      not_started: colors.warning,
      in_progress: colors.success,
      paused: colors.danger,
      completed: colors.black,
      cancelled: colors.grey55,
    })[status]};
`;

function MemberProjectsTab({ member }) {
  useDocumentTitle(`${member.name} Projects`);

  const api = useApi();
  const { workspace } = useWorkspace();
  const history = useHistory();
  const [assignProjectModal, setAssignProjectModal] = useState(false);

  const initialParams = useMemo(() => {
    return {
      q: '',
      clients: [],
      statuses: [],
      recordStatusId: 'active',
      sort: new QuerySort('name', 'asc'),
    };
  }, []);

  const searchParamsConfig = useSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        clients: searchParamsConfig.clients,
        statuses: searchParamsConfig.projectStatuses,
        recordStatusId: { default: 'active', ...searchParamsConfig.projectRecordStatusId },
        sort: { default: initialParams.sort, ...searchParamsConfig.sort },
      }),
      [searchParamsConfig, initialParams],
    ),

    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams(params);
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const [query, setQuery] = useState({ isReady: false, data: null });

  const [params, setParams] = useState(initialParams);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .members(member.id)
        .projects.assigned({
          q: params.q,
          clientId: params.clients?.map((v) => v.id),
          statusId: params.statuses?.map((v) => v.id),
          recordStatusId: params.recordStatusId ?? undefined,
          sort: params.sort,
        });

      setQuery({ isReady: true, data });
    } catch {
      setQuery({ isReady: true, data: [] });
    }
  }, [workspace.id, member.id, params, api]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const handleFilter = (value) => {
    const filters = { ...params, ...value };
    setParams(filters);
    searchParams.set({ ...value });
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    handleFilter({ sort: querySort });
  };

  const handleAssignProjectClick = () => {
    setAssignProjectModal(true);
  };

  const handleProjectAssigned = () => {
    fetchData();
    setAssignProjectModal(false);
  };

  if (!query.isReady) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Projects</Page.Title>

        <Page.Actions>
          <IconButton icon="plus" tooltip="Assign Project" onClick={handleAssignProjectClick} />
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SearchInput
            value={params.q}
            placeholder="Search"
            onChange={({ target: { value } }) => handleFilter({ q: value })}
          />

          <ClientFilter
            name="clients"
            activeOnly={false}
            value={params.clients}
            onChange={({ target: { value } }) => handleFilter({ clients: value })}
          />

          <ProjectStatusFilter
            name="statuses"
            placeholder="Project Status"
            value={params.statuses}
            onChange={({ target: { value } }) => handleFilter({ statuses: value })}
          />

          <SingleSelect
            name="recordStatusId"
            placeholder="Project Archived"
            value={params.recordStatusId}
            showEmptyOption
            onChange={({ target: { value } }) => handleFilter({ recordStatusId: value })}>
            <option value="active">No</option>
            <option value="archived">Yes</option>
          </SingleSelect>
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column name="name" minWidth="16rem" sticky onSort={handleSort} sort={params.sort}>
              Name
            </ListView.Column>

            <ListView.Column name="code" minWidth="16rem" onSort={handleSort} sort={params.sort}>
              Code
            </ListView.Column>

            <ListView.Column width="10rem">Status</ListView.Column>

            <ListView.Column width="16rem">Project Admin</ListView.Column>

            <ListView.Column>Roles</ListView.Column>

            <ListView.Column width="6rem" align="right" name="hoursWorked" onSort={handleSort} sort={params.sort}>
              Hours
            </ListView.Column>

            <ListView.Column name="createdAt" onSort={handleSort} sort={params.sort} width="8rem" align="right">
              Created
            </ListView.Column>

            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {query.data.map((project) => {
              const handleView = () => {
                history.push(`/app/${workspace.key}/portfolio/projects/${project.client.key}/${project.key}`);
              };

              const editPath = `/app/${workspace.key}/portfolio/projects/${project.client.key}/${project.key}/settings`;
              const handleEdit = (event) => {
                if (event.metaKey || event.ctrlKey) window.open(editPath, '_blank', 'noopener');
                else history.push(editPath);
              };

              return (
                <ListView.Row key={project.id} onClick={handleView}>
                  <ListView.Cell>
                    <ProjectInfo>
                      <div style={{ display: 'flex' }}>
                        <ProjectLink project={project} onClick={(e) => e.stopPropagation()} />
                      </div>
                      <small>
                        {project.client.name}
                        {project.client.isInternal && <InternalClientTooltip />}
                      </small>
                    </ProjectInfo>
                  </ListView.Cell>

                  <ListView.Cell style={{ wordBreak: 'break-word' }}>{project.code}</ListView.Cell>

                  <ListView.Cell>
                    <Status status={project.status.id}>{project.status.name}</Status>
                  </ListView.Cell>

                  <ListView.Cell>
                    <ProjectAdmins project={project} />
                  </ListView.Cell>

                  <ListView.Cell>
                    <Tags>
                      <FirstRole project={project} />
                      <Roles project={project} />
                    </Tags>
                  </ListView.Cell>

                  <ListView.Cell>
                    <Hours value={project.hoursWorked} />
                  </ListView.Cell>

                  <ListView.Cell>
                    <DateTime value={project.createdAt} />
                  </ListView.Cell>

                  <ListViewActions>
                    <ListViewActions.View onClick={handleView} />

                    <hr />

                    <ListViewActions.Edit onClick={handleEdit} />
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {query.data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={query.data.length} label="Project" />
        </ListView>
      </Page.ListView>

      {assignProjectModal && (
        <AssignProjectModal
          member={member}
          onClose={() => setAssignProjectModal(false)}
          onSaved={handleProjectAssigned}
        />
      )}
    </>
  );
}

const FirstRole = ({ project }) => {
  if (!project.useRoles) return null;

  const role = project.roles[0];
  if (!role) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <BillableIcon value={project.isBillable && role.isBillable} />
      <small>{role.name}</small>
    </Tag>
  );
};

const Title = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

const Roles = ({ project }) => {
  if (!project.useRoles) return null;

  let rolesCount = -1; // Remove the first role because it already shows a tag
  rolesCount += project.roles.length; // Add roles
  if (rolesCount <= 0) return null;

  return (
    <Tooltip
      style={{ display: 'flex' }}
      message={
        <div style={{ fontSize: '1rem' }}>
          <Title>Roles</Title>

          {project.roles.map((role) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={role.id}>
              <BillableIcon value={project.isBillable && role.isBillable} />
              <small>{role.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{rolesCount}</small>
      </Tag>
    </Tooltip>
  );
};

export default MemberProjectsTab;
